import React, { useState, useEffect } from 'react';
import __ from '../internetLanguage';
import config from '../internetConfig';
import utils, { stop } from '../../utils';
import searchStates from '../../searchStates';
import metrics from '../internetMetrics';


export default function ResultsSummary(props) {
	const isAdvanced = Boolean(props.isAdvanced);
	const form = Object.assign({}, props && props.form);
	const data = Object.assign({}, form.data);
	const addons = props.addons;
	const isEmpty = props.isEmpty;
	const resultType = String(props.resultType || 'results');
	const page = parseInt(props.page || 1, 10);
	const totalResults = parseInt(props.totalResults, 10);
	const searchState = parseInt(props.searchState, 10);
	const topicMatches = props.topicMatches;
	const totalPages = Math.ceil(totalResults / config.pageSize);
	const close = <i className="cdc-fa-xmark"></i>;
	const messages = [];

	var siteName = utils.cleanString(utils.getParam('sitename') || '');

	const suggestions = form.suggestions;
	const corrected = form.corrected;

	// don't display if form is empty or we're searching
	if (isEmpty || searchState === searchStates.SEARCHING || searchState === searchStates.READY) {
		return '';
	}

	// build out tags
	var tags = ['all', 'exact', 'any', 'none'];
	if (isAdvanced) {
		if ( resultType === 'journals' || resultType === 'podcasts' ) {
			tags = tags.concat(['date1', 'date2', 'dateRange']);
		} else {
			tags = tags.concat(['url', 'language', 'date1', 'date2', 'audience', 'topic', 'dateRange']);
		}
	}

	const buildTag = (tag, tagIndex) => {
		let value = data[tag] || '';
		if (['audience', 'topic'].includes(tag)) {
			value = data.checkedFilters?.[tag];
			if (!Array.isArray(value) || !value.length) {
				return '';
			}
			return value.map((val, valIndex) => {
				return getTag('', val, `${tag}-${valIndex}`, () => clearTag(tag, val));
			});
		}
		if (!value) {
			return '';
		}
		if ('date1' === tag && (utils.cleanString(data.date1) || utils.cleanString(data.date2))) {
			let from = data.date1 ? data.date1 : __('anyDate');
			let to = data.date2 ? data.date2 : __('anyDate');
			return getTag(__('dates'), from + ' - ' + to, tagIndex, () => clearTag('dates'));
		}
		if ('dateRange' ===tag) {
			return '';
		}
		// we only display date1 to handle both, a compound tag
		if ('date2' === tag) {
			return '';
		}
		if ('language' === tag) {
			return getTag(__.ucFirst(tag), __('lang' + value), tagIndex, () => clearTag(tag));
		}
		return getTag(__(tag), value, tagIndex, () => clearTag(tag));
	};

	// fetch actual tag output for given values
	const getTag = (label, value, key, onClick) => {
		return (
			<div key={key} className="tag-item tag-item__pill">
				<button
					className="tag"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onClick();
					}}
					onKeyUp={(e) => {
						if ('Enter' === e.key) {
							e.preventDefault();
							e.stopPropagation();
							onClick();
						}
					}}
					type="button"
					aria-label={`Remove ${label} filter: ${value}`}
				>
					{ label ? `${__.ucFirst(label)}: ` : ' ' }
					{value} {close}{' '}
				</button>
			</div>
		);
	};

	const clearTag = (tag, value) => {
		switch (tag) {
			case 'query':
				form.setQuery('');
				break;
			case 'all':
				form.setAll('');
				break;
			case 'any':
				form.setAny('');
				break;
			case 'exact':
				form.setExact('');
				break;
			case 'none':
				form.setNone('');
				break;
			case 'language':
				form.setLanguage('');
				break;
			case 'url':
				form.setUrl('');
				break;
			case 'dates':
			case 'date1':
			case 'date2':
				form.setDateRange('0');
				form.setDate1('');
				form.setDate2('');
				break;
			case 'clearFilter':
			case 'clear':
				form.setAll('');
				form.setAny('');
				form.setExact('');
				form.setNone('');
				form.setDate1('');
				form.setDate2('');
				form.setDateRange('0');
				form.setLanguage('');
				form.setUrl('');
				form.setStrict('');
				form.setCheckedFilters({topic: [], audience: [], contenttype: []});
				break;
			case 'topic':
			case 'audience':
				let updatedCheckedFilters = Object.assign({}, data.checkedFilters);
				updatedCheckedFilters[tag] = updatedCheckedFilters[tag].filter(item => item !== value);
				form.setCheckedFilters(updatedCheckedFilters);
				break;
			default:
				return;
		}
		form.doSearch();
	};

	// build tags
	const outputTags = [];
	tags.map((tag, tagIndex) => {
		let outputTag = buildTag(tag, tagIndex);
		if (outputTag) {
			// Handle both single elements and arrays of elements
			if (Array.isArray(outputTag)) {
				outputTags.push(...outputTag);
			} else {
				outputTags.push(outputTag);
			}
		}
	});

	// get site name
	if (data.siteLimit && 'cdc' !== data.siteLimit) {
		if (-1 < data.siteLimit.indexOf('.')) {
			// when siteLimit has a period, it's probably ATSDR
			siteName = data.siteLimit.replace(/\..*/, '').toUpperCase();
		} else {
			siteName = utils.titleCase(data.siteLimit).replace(/[\/\|].*/, '');
		}
	}

	// if using cioSearchs addon, render results
	if (addons && addons.cioMatches && addons.cioMatches.length) {
		let cios = [];
		addons.cioMatches.forEach((cio, i) => {
			if (cio.abbr && cio.url) {
				cios.push(
					<a href={cio.url} key={'cios' + i}>
						{cio.abbr}
					</a>
				);
			}
		});
		if (cios.length) {
			messages.push(
				<p key="cios">
					{__('related') + ': '}
					{utils.jsxJoin(cios)}
				</p>
			);
		}
	}

	// redirect to all site results
	const allSiteResultsUrl = () => {
		let updated = Object.assign({}, data, { siteLimit: 'cdc' });
		return utils.buildQueryParams(updated);
	};

	const newSearch = (term, strictSearch) => {
		form.setQuery(term);
		form.setStrict(strictSearch ? 'true' : '');
		form.doSearch();
	};

	return (
		<div className="results-summary">
		{outputTags.length ? (
				<div className="tags">
					{outputTags.map((tag, index) =>
						React.cloneElement(tag, { key: tag.key || index })
					)}
					<button
						className="search-terms-clear"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							clearTag('clearFilter');
						}}
						type="button"
						aria-label="Clear all filters"
					>
						{__.ucFirst('clearAll')}
					</button>
				</div>
			) : (
				''
			)}
			<div className="results-heading">
				{(() => {
					if (!totalResults) {
						return (
							<>
								<h3 className='no-results__header'>{__('noResultsMessage')}</h3>
								<span className="results-tag" key="1">
									{' '}
									{__.ucFirst('noResults', [resultType])}{' '}
								</span>
							</>
						);
					}
					if (1 === page) {
						return (
							<span className="results-tag" key="2">
								{' '}
								{__.ucFirst('resultsFor', [resultType])}{' '}
							</span>
						);
					}
					return (
						<span className="results-tag" key="3">
							{' '}
							{__.ucFirst('pageReturnedFor', [page, resultType])}{' '}
						</span>
					);
				})()}
				{data.query ? <span className="tag tag-no-hover">{data.query}</span> : ''}
				<span>
					{(() => {
						if ( !config.isSpanish ) {
							return (<><strong> from</strong> {siteName ? siteName + ' site ' : 'all of CDC.gov'}</> );
						} else {
							return (<><strong> del sitio web de </strong> {siteName ?  siteName : 'CDC.gov'}</> );
						}
					})()}
				</span>
			</div>


			{corrected ? (
				<p>
					{__('searchInstead') + ' '}
					<a href="#results" className="search-instead--link"  onClick={(e) => stop(e) && newSearch(corrected, true)}>
						{corrected}
					</a>
				</p>
			) : (
				''
			)}

			{suggestions && suggestions.length ? (
				<p>
					{__('Also try') + ': '}
					{suggestions
						.map((suggestion, i) => (
							<a href="#results" className="also-try--link" key={i} onClick={(e) => stop(e) && newSearch(suggestion)}>
								{suggestion}
							</a>
						))
						.reduce((acc, x) => (null === acc ? [x] : [acc, ', ', x]), null)}
				</p>
			) : (
				''
			)}
			{topicMatches?.children && topicMatches.children?.length ? (
				<p>
					{__('Related topics') + ': '}
					{topicMatches.children
						.map((match, i) => (
							<a href="#results" className="related-topic--link" key={i} onClick={(e) => stop(e) && metrics.event(e, 'cdcsitesearch-tax', {eVar63: match, prop40: 'cdcsitesearch-tax'}) && newSearch(match, true)}>
								{match}
							</a>
						))
						.reduce((acc, x) => (null === acc ? [x] : [acc, ', ', x]), null)}
				</p>
			) : (
				''
			)}

			{isAdvanced && config.isEnglish && siteName ? (
				<div className="site-limit-msg">
					<a href={allSiteResultsUrl()}>
						<p>
							<span className='all-sites-msg'>Show results from all sites</span>
						</p>
					</a>
				</div>
			) : (
				''
			)}

			{messages}

		</div>
	);
}
